import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useApiMutation } from "../../api/hooks";
import { ErrorState } from "../../components/ui";
import { CHECKER_LABEL, CHECKER_URL } from "../../lib/links";

const MOCK = import.meta.env.VITE_MOCK === "1";

/** Where the next path from the query string is safe to follow: same-origin paths only. */
export function safeNext(next: string | null, fallback = "/vendors"): string {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : fallback;
}

/** "Check your inbox", shared by sign-in and sign-up: the link is the same magic link either way. */
export function LinkSent({ to, next, onReset }: { to: string; next: string; onReset: () => void }) {
  return (
    <div className="callout ok stack" role="status" style={{ gap: "var(--space-3)" }}>
      <p style={{ margin: 0 }}>
        <strong>Check your inbox.</strong> We sent a sign-in link to {to}. It works once and expires in 15
        minutes.
      </p>
      {MOCK && (
        <p style={{ margin: 0 }}>
          <Link
            className="btn primary"
            to={`/auth/verify?token=mock-${Date.now().toString(36)}-token&next=${encodeURIComponent(next)}`}
          >
            Open the sign-in link (mock)
          </Link>
        </p>
      )}
      <p style={{ margin: 0 }}>
        <button className="btn small" onClick={onReset}>
          Use a different email
        </button>
      </p>
    </div>
  );
}

/**
 * One email field and one button: POST /v1/auth/magic-link. Used by sign-in and sign-up; the verify
 * step decides whether the address joins an existing workspace or starts a new trial.
 */
export function MagicLinkForm({
  next,
  submitLabel,
  pendingLabel = "Sending…",
  after,
}: {
  next: string;
  submitLabel: string;
  pendingLabel?: string;
  after?: ReactNode;
}) {
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const request = useApiMutation("auth.magicLink");
  const emailId = useId();

  if (sentTo) return <LinkSent to={sentTo} next={next} onReset={() => setSentTo(null)} />;
  return (
    <form
      className="stack"
      style={{ gap: "0.625rem" }}
      onSubmit={(e) => {
        e.preventDefault();
        request.mutate({ body: { email: email.trim(), next } }, { onSuccess: () => setSentTo(email.trim()) });
      }}
    >
      <label htmlFor={emailId} className="label">
        Work email
      </label>
      <div className="inline-form">
        <input
          id={emailId}
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
        />
        <button className="btn primary lg" type="submit" disabled={request.isPending}>
          {request.isPending ? pendingLabel : submitLabel}
        </button>
      </div>
      {request.error && <ErrorState error={request.error} />}
      {after}
    </form>
  );
}

export function LoginPage() {
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));

  return (
    <div className="auth">
      <div>
        <h1>Sign in to CheckOne.</h1>
        <p className="lede">We email you a one-time link. No password to remember.</p>
      </div>
      <MagicLinkForm next={next} submitLabel="Email me a sign-in link" />
      <div className="steps-3" style={{ gridTemplateColumns: "1fr" }}>
        <div>
          <strong>New to CheckOne?</strong>
          <span>
            <Link to="/signup">Start free</Link> with your work email. 14 days, no card.
          </span>
        </div>
        <div>
          <strong>Want to check one vendor first?</strong>
          <span>
            The free checker at <a href={CHECKER_URL}>{CHECKER_LABEL}</a> needs no account.
          </span>
        </div>
      </div>
    </div>
  );
}

export function VerifyPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const next = safeNext(params.get("next"));
  const verify = useApiMutation("auth.verify");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    verify.mutate(
      { body: { token } },
      {
        onSuccess: (data) => {
          qc.setQueryData(["auth.me", {}], data);
          void navigate(next, { replace: true });
        },
      },
    );
  }, [token, next, verify, navigate, qc]);

  return (
    <div className="auth">
      <h1>Signing you in</h1>
      {verify.error ? (
        <div className="stack">
          <ErrorState error={verify.error} />
          <p>
            <Link className="btn primary" to="/login">
              Request a new link
            </Link>
          </p>
        </div>
      ) : (
        <p className="lede" role="status">
          Checking your link…
        </p>
      )}
    </div>
  );
}
